import moment from 'moment';

import {
  DensityRange1HourByMinutes,
  DensityRange4HoursByHour,
  DensityRange4HoursByMinutes,
  DensityRange12HoursByHour,
  DensityRange15Minutes,
  DensityRange24HoursByHour,
  DensityRange30Minutes,
  DensityRangeWeekByHour,
} from '~/src/dash/globals/constants';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { TKey } from '~/src/dash/publicaUI';

export type DateRange =
  | 'Custom'
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'This Week'
  | 'Last Week'
  | 'Last 30 Days'
  | 'This Month'
  | 'Last Month'
  | 'Tomorrow'
  | 'Next 7 Days'
  | 'Rest Of Week'
  | 'Next Week'
  | 'Next 30 Days'
  | 'Next Month';

export enum DateRangeInt {
  DateRangeUnknown = 0,
  DateRangeToday,
  DateRangeYesterday,
  DateRangeLast7Days,
  DateRangeThisWeek,
  DateRangeLastWeek,
  DateRangeLast30Days,
  DateRangeThisMonth,
  DateRangeLastMonth,
  DateRangeTomorrow,
  DateRangeNext7Days,
  DateRangeRestOfWeek,
  DateRangeNextWeek,
  DateRangeNext30Days,
  DateRangeNextMonth,
}

export const DateRangeToDateRangeInt: Record<DateRange, DateRangeInt> = {
  Custom: DateRangeInt.DateRangeUnknown,
  Today: DateRangeInt.DateRangeToday,
  Yesterday: DateRangeInt.DateRangeYesterday,
  'Last 7 Days': DateRangeInt.DateRangeLast7Days,
  'This Week': DateRangeInt.DateRangeThisWeek,
  'Last Week': DateRangeInt.DateRangeLastWeek,
  'Last 30 Days': DateRangeInt.DateRangeLast30Days,
  'This Month': DateRangeInt.DateRangeThisMonth,
  'Last Month': DateRangeInt.DateRangeLastMonth,
  Tomorrow: DateRangeInt.DateRangeTomorrow,
  'Next 7 Days': DateRangeInt.DateRangeNext7Days,
  'Rest Of Week': DateRangeInt.DateRangeRestOfWeek,
  'Next Week': DateRangeInt.DateRangeNextWeek,
  'Next 30 Days': DateRangeInt.DateRangeNext30Days,
  'Next Month': DateRangeInt.DateRangeNextMonth,
};

export const DateRangeIntToDateRange: Record<DateRangeInt, DateRange> = {
  [DateRangeInt.DateRangeUnknown]: 'Custom',
  [DateRangeInt.DateRangeToday]: 'Today',
  [DateRangeInt.DateRangeYesterday]: 'Yesterday',
  [DateRangeInt.DateRangeLast7Days]: 'Last 7 Days',
  [DateRangeInt.DateRangeThisWeek]: 'This Week',
  [DateRangeInt.DateRangeLastWeek]: 'Last Week',
  [DateRangeInt.DateRangeLast30Days]: 'Last 30 Days',
  [DateRangeInt.DateRangeThisMonth]: 'This Month',
  [DateRangeInt.DateRangeLastMonth]: 'Last Month',
  [DateRangeInt.DateRangeTomorrow]: 'Tomorrow',
  [DateRangeInt.DateRangeNext7Days]: 'Next 7 Days',
  [DateRangeInt.DateRangeRestOfWeek]: 'Rest Of Week',
  [DateRangeInt.DateRangeNextWeek]: 'Next Week',
  [DateRangeInt.DateRangeNext30Days]: 'Next 30 Days',
  [DateRangeInt.DateRangeNextMonth]: 'Next Month',
};

interface IQueryDates {
  startDate: moment.Moment;
  endDate: moment.Moment;
  compareStartDate: moment.Moment;
  compareEndDate: moment.Moment;
}

export const generateQueryDates = (
  dateRange: DateRange,
  timezone: string,
  isExcludingToday?: boolean,
): IQueryDates => {
  const endOfToday = moment().tz(timezone).endOf('day');
  switch (dateRange) {
    case 'Today':
      return {
        startDate: moment().tz(timezone).startOf('day'),
        endDate: endOfToday,
        compareStartDate: moment().tz(timezone).subtract(1, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).subtract(1, 'd').endOf('day'),
      };
    case 'Yesterday':
      return {
        startDate: moment().tz(timezone).subtract(1, 'd').startOf('day'),
        endDate: moment().tz(timezone).subtract(1, 'd').endOf('day'),
        compareStartDate: moment().tz(timezone).subtract(2, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).subtract(2, 'd').endOf('day'),
      };
    case 'This Week':
      const weekStartDate = moment().tz(timezone).weekday(0).startOf('day');
      return {
        startDate: weekStartDate,
        endDate: endOfToday,
        compareStartDate: moment(weekStartDate).subtract(1, 'week'),
        compareEndDate: moment(endOfToday).subtract(1, 'week'),
      };
    case 'Last Week':
      const lastWeekStartDate = moment().tz(timezone).weekday(0).startOf('day').subtract(1, 'week');
      const lastWeekEndDate = lastWeekStartDate.clone().add(6, 'd').endOf('day');
      return {
        startDate: lastWeekStartDate,
        endDate: lastWeekEndDate,
        compareStartDate: moment(lastWeekStartDate).subtract(1, 'week'),
        compareEndDate: moment(lastWeekEndDate).subtract(1, 'week'),
      };
    case 'Last 30 Days':
      return {
        startDate: moment()
          .tz(timezone)
          .subtract(isExcludingToday ? 30 : 29, 'd')
          .startOf('day'),
        endDate: endOfToday.subtract(isExcludingToday ? 1 : 0, 'd'),
        compareStartDate: moment()
          .tz(timezone)
          .subtract(isExcludingToday ? 60 : 59, 'd')
          .startOf('day'),
        compareEndDate: moment()
          .tz(timezone)
          .subtract(isExcludingToday ? 31 : 30, 'd')
          .endOf('day'),
      };
    case 'This Month':
      const monthStartDate = moment().tz(timezone).startOf('month');
      const monthEndDate = moment().tz(timezone).endOf('day');
      return {
        startDate: monthStartDate,
        endDate: monthEndDate,
        compareStartDate: moment(monthStartDate).subtract(1, 'month'),
        compareEndDate: moment(monthEndDate).subtract(1, 'month'),
      };
    case 'Last Month':
      const lastMonthStartDate = moment().tz(timezone).subtract(1, 'month').startOf('month');
      const lastMonthEndDate = moment()
        .tz(timezone)
        .subtract(1, 'month')
        .endOf('month')
        .endOf('day');
      return {
        startDate: lastMonthStartDate,
        endDate: lastMonthEndDate,
        compareStartDate: moment(lastMonthStartDate).subtract(1, 'month'),
        compareEndDate: moment(lastMonthEndDate).subtract(1, 'month'),
      };
    case 'Tomorrow':
      return {
        startDate: moment().tz(timezone).add(1, 'd').startOf('day'),
        endDate: moment().tz(timezone).add(1, 'd').endOf('day'),
        compareStartDate: moment().tz(timezone).add(2, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).add(2, 'd').endOf('day'),
      };
    case 'Next 7 Days':
      return {
        startDate: moment().tz(timezone).startOf('day'),
        endDate: moment().tz(timezone).add(6, 'd').endOf('day'),
        compareStartDate: moment().tz(timezone).add(7, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).add(13, 'd').endOf('day'),
      };
    case 'Rest Of Week':
      const restOfWeekStartDate = moment().tz(timezone).add(1, 'd').startOf('day');
      const restOfWeekEndDate = restOfWeekStartDate.clone().endOf('week');
      return {
        startDate: restOfWeekStartDate,
        endDate: restOfWeekEndDate,
        compareStartDate: moment().tz(timezone).add(1, 'week').startOf('week'),
        compareEndDate: moment()
          .tz(timezone)
          .add(1, 'week')
          .startOf('week')
          .add(restOfWeekEndDate.diff(restOfWeekStartDate, 'days'), 'days')
          .endOf('day'),
      };
    case 'Next Week':
      const nextWeekStartDate = moment().tz(timezone).weekday(0).startOf('day').add(1, 'week');
      const nextWeekEndDate = nextWeekStartDate.clone().add(6, 'd').endOf('day');
      return {
        startDate: nextWeekStartDate,
        endDate: nextWeekEndDate,
        compareStartDate: moment(nextWeekStartDate).add(1, 'week'),
        compareEndDate: moment(nextWeekEndDate).add(1, 'week'),
      };
    case 'Next 30 Days':
      const today = moment().tz(timezone).startOf('day');
      const thirtyDays = moment().tz(timezone).add(29, 'd').endOf('day');
      return {
        startDate: today,
        endDate: thirtyDays,
        compareStartDate: moment(today).add(1, 'month'),
        compareEndDate: moment(thirtyDays).add(1, 'month'),
      };
    case 'Next Month':
      const nextMonthStartDate = moment().tz(timezone).add(1, 'month').startOf('month');
      const nextMonthEndDate = moment().tz(timezone).add(1, 'month').endOf('month').endOf('day');
      return {
        startDate: nextMonthStartDate,
        endDate: nextMonthEndDate,
        compareStartDate: moment(nextMonthStartDate).add(1, 'month'),
        compareEndDate: moment(nextMonthEndDate).add(1, 'month'),
      };
    // Last 7 Days default
    case 'Last 7 Days':
    default:
      return {
        startDate: moment()
          .tz(timezone)
          .subtract(isExcludingToday ? 7 : 6, 'd')
          .startOf('day'),
        endDate: isExcludingToday ? endOfToday.subtract(1, 'd') : endOfToday,
        compareStartDate: moment()
          .tz(timezone)
          .subtract(isExcludingToday ? 14 : 13, 'd')
          .startOf('day'),
        compareEndDate: moment()
          .tz(timezone)
          .subtract(isExcludingToday ? 8 : 7, 'd')
          .endOf('day'),
      };
  }
};

export interface IDateTimeRange {
  key: DateRange | TimeRange;
  tKey?: TKey;
  shortTKey?: TKey;
  label: string;
  shortLabel: string;
  extendedLabel?: string;
}

export interface IDateRange extends IDateTimeRange {
  defaultDateGroup: number;
  dateGroupRange: [number, number];
  generateQueryDates: (timezone: string, isExcludingToday: boolean) => IQueryDates;
  isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => boolean;
  getDateRange: (timezone?: string) => number;
}

export const defaultDateRanges: IDateRange[] = [
  {
    key: 'Today',
    tKey: 'Today',
    label: 'Today',
    shortLabel: 'Today',
    shortTKey: 'Today',
    defaultDateGroup: EDateGroup.ByHour,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByDay],
    generateQueryDates: (timezone: string) => generateQueryDates('Today', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Today', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 1,
  },
  {
    key: 'Yesterday',
    tKey: 'Yesterday',
    label: 'Yesterday',
    shortLabel: 'Yesterday',
    shortTKey: 'Yesterday',
    defaultDateGroup: EDateGroup.ByHour,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByDay],
    generateQueryDates: (timezone: string) => generateQueryDates('Yesterday', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Yesterday', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 1,
  },
  {
    key: 'Last 7 Days',
    tKey: 'LastSevenDays',
    label: 'Last 7 Days',
    shortLabel: 'L7D',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string, isExcludingToday: boolean) =>
      generateQueryDates('Last 7 Days', timezone, isExcludingToday),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last 7 Days', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 7,
  },
  {
    key: 'This Week',
    tKey: 'WeekToDate',
    label: 'Week To Date',
    shortLabel: 'WTD',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('This Week', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Week', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Week', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
  {
    key: 'Last Week',
    tKey: 'LastWeekMonToSun',
    label: 'Last Week (Mon-Sun)',
    shortLabel: 'LW',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('Last Week', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last Week', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 7,
  },
  {
    key: 'Last 30 Days',
    tKey: 'LastThirtyDays',
    label: 'Last 30 Days',
    shortLabel: 'L30D',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string, isExcludingToday: boolean) =>
      generateQueryDates('Last 30 Days', timezone, isExcludingToday),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last 30 Days', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 30,
  },
  {
    key: 'This Month',
    tKey: 'MonthToDate',
    label: 'Month To Date',
    shortLabel: 'MTD',
    defaultDateGroup: EDateGroup.ByMonth,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('This Month', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Month', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Month', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
  {
    key: 'Last Month',
    tKey: 'LastMonth',
    label: 'Last Month',
    shortLabel: 'LM',
    defaultDateGroup: EDateGroup.ByMonth,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('Last Month', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last Month', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last Month', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
];

export const shortDateRanges: IDateRange[] = defaultDateRanges.filter((range) =>
  ['Today', 'Yesterday', 'Last 7 Days'].includes(range.key),
);

// this will never return undefined because all DateRanges are accounted for
// but because .find returns ' | undefined' we use an "impossible" ternary to remove undefined
export const findDateRange = (key: DateRange): IDateRange => {
  return defaultDateRanges.find((range) => key === range.key) || defaultDateRanges[0];
};

export const defaultForecastingDateRanges: IDateRange[] = [
  {
    key: 'Tomorrow',
    label: 'Tomorrow',
    shortLabel: 'Tomorrow',
    defaultDateGroup: EDateGroup.ByHour,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByDay],
    generateQueryDates: (timezone: string) => generateQueryDates('Tomorrow', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string): boolean => {
      const { startDate, endDate } = generateQueryDates('Tomorrow', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 1,
  },
  {
    key: 'Next 7 Days',
    label: 'Next 7 Days',
    shortLabel: 'N7D',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('Next 7 Days', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string): boolean => {
      const { startDate, endDate } = generateQueryDates('Next 7 Days', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 7,
  },
  {
    key: 'Rest Of Week',
    label: 'Rest Of Week',
    shortLabel: 'ROW',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByDay],
    generateQueryDates: (timezone: string) => generateQueryDates('Rest Of Week', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string): boolean => {
      const { startDate, endDate } = generateQueryDates('Rest Of Week', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string): number => {
      const { startDate, endDate } = generateQueryDates('Rest Of Week', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
  {
    key: 'Next Week',
    label: 'Next Week',
    shortLabel: 'NW',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('Next Week', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string): boolean => {
      const { startDate, endDate } = generateQueryDates('Next Week', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 7,
  },
  {
    key: 'Next 30 Days',
    label: 'Next 30 Days',
    shortLabel: 'N30D',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('Next 30 Days', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string): boolean => {
      const { startDate, endDate } = generateQueryDates('Next 30 Days', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 30,
  },
  {
    key: 'Next Month',
    label: 'Next Month',
    shortLabel: 'Next Month',
    defaultDateGroup: EDateGroup.ByMonth,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('Next Month', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string): boolean => {
      const { startDate, endDate } = generateQueryDates('Next Month', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string): number => {
      const { startDate, endDate } = generateQueryDates('Next Month', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
];

// this will never return undefined because all DateRanges are accounted for
// but because .find returns ' | undefined' we use an "impossible" ternary to remove undefined
export const findForecastingDateRange = (key: DateRange): IDateRange => {
  return (
    defaultForecastingDateRanges.find((range) => key === range.key) ||
    defaultForecastingDateRanges[0]
  );
};

export type TimeRange =
  | '15 Minutes'
  | '30 Minutes'
  | '1 Hour By Minute'
  | '1 Hour'
  | '4 Hours'
  | '4 Hours By Minute'
  | '4 Hours By Hour'
  | '12 Hours'
  | '12 Hours By Hour'
  | '24 Hours'
  | '24 Hours By Hour'
  | 'Week By Hour';
// | '12 Hours By Half Hour'; // TODO do I need this

export interface ITimeRange extends IDateTimeRange {
  value: number;
}

export const defaultTimeRanges: ITimeRange[] = [
  {
    key: '15 Minutes',
    tKey: 'FifteenMinutes',
    value: DensityRange15Minutes,
    label: '15 Mins',
    shortLabel: '15 MIN',
    extendedLabel: '15 Minutes',
  },
  {
    key: '30 Minutes',
    tKey: 'ThirtyMinutes',
    value: DensityRange30Minutes,
    label: '30 Mins',
    shortLabel: '30 MIN',
    extendedLabel: '30 Minutes',
  },
  {
    key: '1 Hour By Minute',
    tKey: 'OneHourByMinute',
    value: DensityRange1HourByMinutes,
    label: '1 Hour',
    shortLabel: '1 HR',
    extendedLabel: '1 Hour By Minute',
  },
  {
    key: '4 Hours By Minute',
    tKey: 'FourHoursByMinute',
    value: DensityRange4HoursByMinutes,
    label: '4 Hours',
    shortLabel: '4 HRS',
    extendedLabel: '4 Hours By Minute',
  },
  {
    key: '4 Hours By Hour',
    tKey: 'FourHoursByHour',
    value: DensityRange4HoursByHour,
    label: '4 Hours By Hour',
    shortLabel: '4 HRS BY HR',
    extendedLabel: '4 Hours By Hour',
  },
  {
    key: '12 Hours By Hour',
    tKey: 'TwelveHoursByHour',
    value: DensityRange12HoursByHour,
    label: '12 Hours By Hour',
    shortLabel: '12 HRS BY HR',
    extendedLabel: '12 Hours By Hour',
  },
  {
    key: '24 Hours By Hour',
    tKey: 'TwentyFourHoursByHour',
    value: DensityRange24HoursByHour,
    label: '24 Hours By Hour',
    shortLabel: '24 HRS BY HR',
    extendedLabel: '24 Hours By Hour',
  },
  {
    key: 'Week By Hour',
    tKey: 'WeekByHour',
    value: DensityRangeWeekByHour,
    label: 'Week By Hour',
    shortLabel: 'WEEK BY HR',
    extendedLabel: 'Week By Hour',
  },
];

export const auctionInsightsTimeRanges: ITimeRange[] = [
  {
    key: '15 Minutes',
    tKey: 'FifteenMinutes',
    label: '15 Minutes',
    shortLabel: '15 MIN',
    extendedLabel: '15 Minutes',
    value: DensityRange15Minutes,
  },
  {
    key: '30 Minutes',
    tKey: 'ThirtyMinutes',
    label: '30 Minutes',
    shortLabel: '30 MIN',
    extendedLabel: '30 Minutes',
    value: DensityRange30Minutes,
  },
  {
    key: '1 Hour',
    tKey: 'OneHour',
    label: '1 Hour',
    shortLabel: '1 HR',
    extendedLabel: '1 Hour',
    value: DensityRange1HourByMinutes,
  },
  {
    key: '4 Hours',
    tKey: 'FourHours',
    value: DensityRange4HoursByHour,
    label: '4 Hours',
    shortLabel: '4 HRS',
    extendedLabel: '4 Hours',
  },
  {
    key: '12 Hours',
    tKey: 'TwelveHours',
    value: DensityRange12HoursByHour,
    label: '12 Hours ',
    shortLabel: '12 HRS',
    extendedLabel: '12 Hours',
  },
  {
    key: '24 Hours',
    tKey: 'TwentyFourHours',
    value: DensityRange24HoursByHour,
    label: '24 Hours',
    shortLabel: '24 HRS',
    extendedLabel: '24 Hours',
  },
];

export const findTimeRange = (value: number, ranges = defaultTimeRanges) => {
  return ranges.find((range) => value === range.value) || ranges[0];
};

export const findTimeRangeByKey = (key: string, ranges = defaultTimeRanges) => {
  return ranges.find((range) => key === range.key) || ranges[0];
};

export const findAuctionInsightsTimeRangeByKey = (
  key: string,
  ranges = auctionInsightsTimeRanges,
): ITimeRange => {
  return ranges.find((range) => key === range.key) || ranges[0];
};
